/**
 * V70-J — 게임대회 공통 페이지 헤더 (e스포츠 스타일)
 *
 * 네이비(#101827) 배경 + 골드(#F6C76B) 포인트, 상단 브랜드 · 흰색 제목 · 연회색 부제.
 * V70-K — 트로피 아이콘 제거. 페이지 바깥 배경을 같은 네이비로 통일(TOURNAMENT_PAGE_STYLE)하면서
 *   헤더는 상자 테두리·그림자 대신 하단 골드 구분선으로 본문과 자연스럽게 이어지게 함.
 * 게임대회 페이지(대시보드·참가신청·게임센터·대진표·리더보드)의 기존 단순 텍스트 제목을 대체한다.
 * 페이지 제목(h1)은 이 헤더 하나만 — 각 페이지의 기존 h1 은 제거하거나 본문 소제목(h2)으로 낮춘다.
 * ERP 전체 테마는 건드리지 않으며 애니메이션·네온 효과 없음. 좁은 화면에서는 줄바꿈 (가로 스크롤 없음).
 */
import React from 'react';
import { DARK } from './tournamentDarkTheme';

export const TOURNAMENT_HEADER_BRAND = 'NGLE CHAMPIONSHIP 2026';
export const TOURNAMENT_HEADER_COLORS = { bg: '#101827', gold: '#F6C76B', title: '#FFFFFF', subtitle: '#A9B4C6' } as const;

export const TOURNAMENT_HEADER_COPY = {
  dashboard: { title: '대회 현황', subtitle: '2026 엔글 게임대회의 모든 소식을 한눈에' },
  apply: { title: '참가신청', subtitle: '도전할 준비가 되셨나요? 지금 대회에 참가하세요.' },
  games: { title: 'GAME ZONE', subtitle: '연습부터 공식 대결까지, 승부를 시작하세요.' },
  bracket: { title: 'MATCH BRACKET', subtitle: '승리를 향한 여정, 대진표를 확인하세요.' },
  leaderboard: { title: 'LEADERBOARD', subtitle: '최고의 플레이어는 누구일까요?' },
} as const;

export type TournamentHeaderPage = keyof typeof TOURNAMENT_HEADER_COPY;

interface Props {
  page: TournamentHeaderPage;
  /** 헤더 오른쪽 보조 버튼 (예: 새로고침·게임센터로). 좁은 화면에서는 제목 아래로 내려감 */
  actions?: React.ReactNode;
  style?: React.CSSProperties;
  /** V70-L — 'dark': 다크 테마. V70-M — 6개 페이지 전체 다크 적용으로 기본값 'dark' ('navy' 는 V70-K 표시, 현재 사용처 없음) */
  tone?: 'navy' | 'dark';
}

/** V70-L — 다크 톤 덮어쓰기 (배경·구분선·브랜드 골드·제목·부제) */
const DARK_TONE = {
  header: { background: DARK.page, borderBottom: `1px solid ${DARK.border}` } as React.CSSProperties,
  brand: { color: DARK.gold } as React.CSSProperties,
  title: { color: DARK.text } as React.CSSProperties,
  subtitle: { color: DARK.textMutedOnPage } as React.CSSProperties,
};

export function TournamentPageHeader({ page, actions, style, tone = 'dark' }: Props) {
  const copy = TOURNAMENT_HEADER_COPY[page];
  const dark = tone === 'dark';
  return (
    <header style={{ ...headerStyle, ...(dark ? DARK_TONE.header : null), ...style }} data-testid="tournament-page-header" data-tone={tone}>
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        <div style={{ ...brandStyle, ...(dark ? DARK_TONE.brand : null) }}>{TOURNAMENT_HEADER_BRAND}</div>
        <h1 style={{ ...titleStyle, ...(dark ? DARK_TONE.title : null) }}>{copy.title}</h1>
        <p style={{ ...subtitleStyle, ...(dark ? DARK_TONE.subtitle : null) }}>{copy.subtitle}</p>
      </div>
      {actions && <div style={actionsStyle}>{actions}</div>}
    </header>
  );
}

const C = TOURNAMENT_HEADER_COLORS;

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
  boxSizing: 'border-box',
  width: '100%',
  padding: '4px 0 16px',
  marginBottom: 20,
  background: C.bg,
  borderBottom: '1px solid rgba(246, 199, 107, 0.28)',
};

/**
 * V70-K — 게임대회 페이지 바깥 틀: 네이비 배경이 본문 영역 끝까지 이어지도록
 *   AppShell <main> 의 고정 여백(32px)만큼 바깥으로 넓히고 같은 만큼 안쪽 여백을 더한다.
 *   AppShell·사이드바·다른 ERP 메뉴는 변경하지 않음. 카드·공지·양식 등 내부 배경은 각 컴포넌트 그대로.
 */
export const TOURNAMENT_SHELL_GUTTER = 32;
/** V70-K — 네이비 배경 위에 바로 놓이는 글자색 (본문 #F1F5F9 대비 16.2:1, 보조 #A9B4C6 대비 8.5:1) */
export const TOURNAMENT_ON_NAVY = { text: '#F1F5F9', muted: TOURNAMENT_HEADER_COLORS.subtitle } as const;
export function tournamentPageStyle(innerPadding: { x: number; y: number }): React.CSSProperties {
  const g = TOURNAMENT_SHELL_GUTTER;
  return {
    minHeight: '100vh',
    // V70-M — 게임대회 6개 페이지 공통 다크 배경 (V70-L 3차 승인 #22262E)
    background: DARK.page,
    margin: -g,
    padding: `${innerPadding.y + g}px ${innerPadding.x + g}px`,
  };
}

const brandStyle: React.CSSProperties = {
  color: C.gold,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 2,
  lineHeight: 1.4,
  overflowWrap: 'anywhere',
};

const titleStyle: React.CSSProperties = {
  margin: '2px 0 0',
  color: C.title,
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: 0.5,
  lineHeight: 1.25,
  // 한글은 단어 단위로 줄바꿈, 그래도 넘치는 긴 단어만 강제 줄바꿈
  wordBreak: 'keep-all',
  overflowWrap: 'anywhere',
};

const subtitleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  color: C.subtitle,
  fontSize: 13,
  lineHeight: 1.5,
  wordBreak: 'keep-all',
  overflowWrap: 'anywhere',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginLeft: 'auto',
};

export default TournamentPageHeader;
